import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const SAMPLE_HOLDINGS = [
  { symbol: 'AAPL', asset_name: 'Apple Inc.', asset_type: 'stock', quantity: 10, average_price: 175.50, current_price: 182.30, sector: 'Technology', source: 'manual' },
  { symbol: 'MSFT', asset_name: 'Microsoft Corp.', asset_type: 'stock', quantity: 5, average_price: 380.00, current_price: 395.20, sector: 'Technology', source: 'manual' },
  { symbol: 'GOOGL', asset_name: 'Alphabet Inc.', asset_type: 'stock', quantity: 8, average_price: 140.00, current_price: 148.50, sector: 'Technology', source: 'manual' },
  { symbol: 'AMZN', asset_name: 'Amazon.com Inc.', asset_type: 'stock', quantity: 6, average_price: 178.00, current_price: 185.60, sector: 'Consumer Cyclical', source: 'manual' },
  { symbol: 'BTC/USD', asset_name: 'Bitcoin', asset_type: 'crypto', quantity: 0.15, average_price: 42000, current_price: 44500, sector: 'Crypto', source: 'manual' },
  { symbol: 'ETH/USD', asset_name: 'Ethereum', asset_type: 'crypto', quantity: 2, average_price: 2200, current_price: 2400, sector: 'Crypto', source: 'manual' },
  { symbol: 'XAU/USD', asset_name: 'Gold', asset_type: 'commodity', quantity: 3, average_price: 1950, current_price: 2020, sector: 'Commodities', source: 'manual' },
  { symbol: 'TSLA', asset_name: 'Tesla Inc.', asset_type: 'stock', quantity: 4, average_price: 240.00, current_price: 255.80, sector: 'Consumer Cyclical', source: 'manual' },
  { symbol: 'NVDA', asset_name: 'NVIDIA Corp.', asset_type: 'stock', quantity: 3, average_price: 480.00, current_price: 520.00, sector: 'Technology', source: 'manual' },
  { symbol: 'JPM', asset_name: 'JPMorgan Chase', asset_type: 'stock', quantity: 7, average_price: 155.00, current_price: 162.40, sector: 'Financial Services', source: 'manual' },
];

export const useOnboardingSetup = () => {
  const { user, updateUserMetadata } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    if (!user || hasRun.current) return;
    if (user.user_metadata?.onboarding_auto_setup) return;
    hasRun.current = true;

    const runSetup = async () => {
      try {
        // 1. Create demo account
        await supabase.functions.invoke('demo-accounts', {
          body: { account_name: 'My Demo Account', initial_balance: 10000 },
        });

        // 2. Seed portfolio holdings (check if already has any)
        const { count } = await supabase
          .from('portfolio_holdings')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (!count || count === 0) {
          const holdings = SAMPLE_HOLDINGS.map((h) => ({
            ...h,
            user_id: user.id,
            market_value: h.quantity * h.current_price,
            unrealized_pnl: h.quantity * (h.current_price - h.average_price),
            unrealized_pnl_pct: ((h.current_price - h.average_price) / h.average_price) * 100,
          }));
          await supabase.from('portfolio_holdings').insert(holdings);
        }

        // 3. Auto-deploy one strategy
        const { count: stratCount } = await supabase
          .from('strategy_instances')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (!stratCount || stratCount === 0) {
          await supabase.from('strategy_instances').insert({
            user_id: user.id,
            strategy_name: 'simple-breakout',
            status: 'running',
            risk_mode: 'medium',
            execution_mode: 'demo',
          });
        }

        // 4. Set flag
        await updateUserMetadata({
          onboarding_auto_setup: true,
          onboarding_completed_steps: ['demo_account', 'portfolio', 'strategy'],
        });
      } catch (err) {
        console.error('[Onboarding] Auto-setup error:', err);
      }
    };

    runSetup();
  }, [user]);
};
